import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Input, Icon, Image, Label, Popup } from 'semantic-ui-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getAccessToken } from '../../utils/access-token-storage';
import { socket } from '../../api';
import selectors from '../../selectors';
import BotActionModal from './BotActionModal/BotActionModal';
import GoogleDriveModal from '../CardModal/AttachmentAddZone/GoogleDriveModal';
import rentalBuddyLogo from '../../assets/images/RentalBuddy SVG Shaka Bold green.svg';
import styles from './Bot.module.scss';

// Action type icons mapping
const ACTION_ICONS = {
  move_card: 'arrows alternate',
  create_card: 'plus square',
  duplicate_card: 'copy',
  add_task: 'tasks',
  create_tasks: 'list',
  add_label: 'tag',
  remove_label: 'tag',
  set_due_date: 'calendar',
  set_description: 'file text',
  add_comment: 'comment',
  get_vehicle_data: 'car',
  update_vehicle_status: 'sync',
  rcm_search_damages: 'search',
  rcm_add_damage: 'warning sign',
  rcm_update_damage: 'wrench',
  rcm_maintenance_report: 'clipboard list',
  update_stopwatch: 'stopwatch',
  send_slack_notification: 'slack',
  search_knowledge_base: 'book',
  rb_get_rental_data: 'handshake',
  rb_get_photos: 'camera',
  rb_get_survey: 'star',
  rb_search_booking: 'search',
};

// Component to display executed actions
const ActionsDisplay = ({ actions }) => {
  const [t] = useTranslation();

  if (!actions || actions.length === 0) return null;

  return (
    <div className={styles.actionsDisplay}>
      <div className={styles.actionsHeader}>
        <Icon name="bolt" />
        <span>{t('common.actionsExecuted', 'Actions Executed')}</span>
      </div>
      <div className={styles.actionsList}>
        {actions.map((action, index) => (
          <Popup
            key={index}
            trigger={
              <Label
                className={`${styles.actionLabel} ${
                  action.success ? styles.actionSuccess : styles.actionFailed
                }`}
                size="small"
              >
                <Icon name={ACTION_ICONS[action.type] || 'cog'} />
                {action.type.replace(/_/g, ' ')}
              </Label>
            }
            content={action.observation || (action.success ? 'Completed' : 'Failed')}
            position="top center"
            size="tiny"
          />
        ))}
      </div>
    </div>
  );
};

const LoadingDots = () => (
  <div className={styles.loadingDots}>
    <span></span>
    <span></span>
    <span></span>
  </div>
);

// Función para extraer datos numéricos de texto (legacy)
const extractDataFromText = text => {
  // Buscar patrones como "Mileage: 199,192 km" o números seguidos de "km"
  const mileagePattern = /(\d{1,3}(?:,\d{3})*)\s*km/g;
  const mileages = [];
  const names = [];

  // Buscar nombres de vehículos (asumiendo que están en formato **Nombre**)
  const namePattern = /\*\*([^*]+)\*\*/g;
  let nameMatch;

  while ((nameMatch = namePattern.exec(text)) !== null) {
    names.push(nameMatch[1]);
  }

  // Extraer valores de kilometraje
  let match;
  while ((match = mileagePattern.exec(text)) !== null) {
    // Convertir "199,192" a 199192
    const value = parseInt(match[1].replace(/,/g, ''), 10);
    mileages.push(value);
  }

  // Si encontramos datos suficientes, devolver un objeto con los datos
  if (mileages.length >= 3 && names.length >= 3) {
    return {
      labels: names.slice(0, 5), // Tomar hasta 5 nombres
      data: mileages.slice(0, 5), // Tomar hasta 5 valores
    };
  }

  return null;
};

// Función para generar URL de QuickChart a partir de datos
const generateChartUrl = chartData => {
  if (!chartData || !chartData.labels || !chartData.data) return null;

  // Colores para las barras
  const colors = ['#36A2EB', '#4BC0C0', '#FF6384', '#FFCE56', '#9966FF'];
  const borderColors = ['#1E88E5', '#00897B', '#D32F2F', '#FBC02D', '#7E57C2'];

  // Configuración del gráfico
  const chartConfig = {
    type: 'bar',
    data: {
      labels: chartData.labels,
      datasets: [
        {
          label: 'Kilometers Driven',
          data: chartData.data,
          backgroundColor: colors.slice(0, chartData.data.length),
          borderColor: borderColors.slice(0, chartData.data.length),
          borderWidth: 1,
        },
      ],
    },
    options: {
      responsive: true,
      scales: {
        x: {
          title: {
            display: true,
            text: 'Van Name',
          },
          beginAtZero: true,
        },
        y: {
          title: {
            display: true,
            text: 'Total Kilometers',
          },
          beginAtZero: true,
          ticks: {
            callback: 'value => value.toLocaleString()',
          },
        },
      },
      plugins: {
        title: {
          display: true,
          text: 'Top 5 Most Used Vans by Kilometers Driven',
        },
        legend: {
          position: 'top',
        },
      },
    },
  };

  // Convertir la configuración a URL de QuickChart
  const chartUrl = `https://quickchart.io/chart?width=400&c=${encodeURIComponent(
    JSON.stringify(chartConfig)
  )}`;
  return chartUrl;
};

// Componente para extraer y renderizar gráficos (legacy)
const ChartRenderer = ({ content, generatedChartUrl }) => {
  const [t] = useTranslation();

  // Si hay una URL de gráfico generada, usarla
  if (generatedChartUrl) {
    return (
      <div className={styles.chartDisplay}>
        <div className={styles.chartContainer}>
          <Image
            src={generatedChartUrl}
            alt={t('common.generatedChart', 'Generated chart')}
            className={styles.chartImage}
          />
          <div className={styles.chartCaption}>
            {t('common.mileageDataVisualization', 'Mileage data visualization')}
          </div>
        </div>
      </div>
    );
  }

  // Si no hay contenido
  if (!content) return null;

  // Extraer URL de QuickChart si existe
  let chartUrl = null;

  if (typeof content === 'string') {
    // Caso 1: El contenido es directamente una URL de QuickChart
    if (content.trim().startsWith('https://quickchart.io/chart')) {
      chartUrl = content.trim();
    } else {
      // Caso 2: La URL está dentro del texto
      const quickChartUrlRegex = /(https:\/\/quickchart\.io\/chart\?[^\s]+)/;
      const match = content.match(quickChartUrlRegex);
      if (match) {
        chartUrl = match[1];
      }
    }
  } else if (content.chartUrl) {
    // Caso 3: El contenido tiene una propiedad chartUrl
    chartUrl = content.chartUrl;
  }

  if (!chartUrl) return null;

  return (
    <div className={styles.chartDisplay}>
      <div className={styles.chartContainer}>
        <Image
          src={chartUrl}
          alt={t('common.generatedChart', 'Generated chart')}
          className={styles.chartImage}
        />
        <div className={styles.chartCaption}>
          {t('common.dataVisualization', 'Data visualization')}
        </div>
      </div>
    </div>
  );
};

// Componente para renderizar texto
const TextRenderer = ({ content }) => {
  const [t] = useTranslation();

  // Si no hay contenido, mostrar mensaje de bienvenida
  if (!content) {
    return (
      <div className={styles.welcomeMessage}>
        {t('common.howCanIHelpYou', '¿Cómo puedo ayudarte hoy?')}
      </div>
    );
  }

  // Contenido a renderizar
  let textToRender = '';

  // Si el contenido es una cadena de texto simple
  if (typeof content === 'string') {
    textToRender = content;
  }
  // Si el contenido tiene texto específico
  else if (content.text) {
    textToRender = content.text;
  }
  // Si el contenido tiene una descripción o caption
  else if (content.caption || content.description) {
    textToRender = content.caption || content.description;
  }
  // Fallback para otros tipos de contenido
  else if (typeof content === 'object') {
    // Intentar extraer texto de alguna propiedad
    const possibleTextProps = ['message', 'content', 'output', 'response'];
    for (const prop of possibleTextProps) {
      if (content[prop] && typeof content[prop] === 'string') {
        textToRender = content[prop];
        break;
      }
    }
  }

  if (!textToRender) {
    return (
      <div className={styles.textContent}>
        {t('common.consultProcessed', 'Query processed. Check the chart to see the results.')}
      </div>
    );
  }

  // Limpiar URLs de QuickChart del texto para mostrar solo el texto
  const cleanText = textToRender.replace(/(https:\/\/quickchart\.io\/chart\?[^\s]+)/g, '');

  return (
    <div className={styles.textContent}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // eslint-disable-next-line react/prop-types
          a: ({ node, children, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {cleanText}
      </ReactMarkdown>
    </div>
  );
};

const STORAGE_KEY = 'bot-chat-messages';

const loadMessages = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    return [];
  }
};

const saveMessages = msgs => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(msgs));
  } catch (e) {
    // sessionStorage full or unavailable
  }
};

const MessageBubble = ({ message }) => {
  if (message.role === 'user') {
    return (
      <div className={styles.userMessage}>
        {message.driveFile && (
          <Label size="mini" className={styles.driveFileLabel} style={{ marginBottom: 6 }}>
            <Icon name="file" />
            {message.driveFile.name}
          </Label>
        )}
        <div className={styles.userMessageText}>{message.content}</div>
      </div>
    );
  }

  return (
    <div className={styles.assistantMessage}>
      <TextRenderer content={message.content} />
      {message.hasChart && (
        <ChartRenderer content={message.content} generatedChartUrl={message.chartUrl} />
      )}
      <ActionsDisplay actions={message.actions} />
    </div>
  );
};

const Bot = () => {
  const [t] = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const dispatch = useDispatch();
  const { boardId, projectId, cardId } = useSelector(selectors.selectPath);

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadMessages());
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isDriveModalOpen, setIsDriveModalOpen] = useState(false);
  const [tempDriveFile, setTempDriveFile] = useState(null);
  const [position, setPosition] = useState({ x: null, y: null });

  const messagesEndRef = useRef(null);
  const dragRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    saveMessages(messages);
  }, [messages]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const lastAssistantMessage = messages.filter(m => m.role === 'assistant').pop();
  const lastAssistantContent = lastAssistantMessage?.content;

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleMinimize = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleNewChat = useCallback(() => {
    setMessages([]);
    setPrompt('');
    setTempDriveFile(null);
  }, []);

  const handlePromptChange = (e, { value }) => {
    setPrompt(value);
  };

  const handleDragStart = useCallback(e => {
    if (e.target.closest('button')) return;
    e.preventDefault();
    const panel = panelRef.current;
    if (!panel) return;

    const rect = panel.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const offsetX = rect.left;
    const offsetY = rect.top;

    const onMouseMove = moveEvent => {
      setPosition({
        x: offsetX + (moveEvent.clientX - startX),
        y: Math.max(0, offsetY + (moveEvent.clientY - startY)),
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const handleSubmit = async () => {
    if (!prompt.trim()) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: prompt,
      driveFile: tempDriveFile ? { name: tempDriveFile.name } : null,
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    const submittedPrompt = prompt;
    setPrompt('');

    try {
      const token = getAccessToken();
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      const payload = {
        prompt: submittedPrompt,
        boardId,
        cardId,
        projectId,
      };

      if (tempDriveFile) {
        payload.driveFileId = tempDriveFile.id;
        payload.driveFileName = tempDriveFile.name;
        payload.driveMimeType = tempDriveFile.mimeType;
      }

      console.log('🤖 [BOT] Sending request...', payload);
      const data = await socket.post('/ai/chat-rag', payload, authHeaders);

      console.log('🤖 [SUPER-BOT] Response:', data);

      let processedResponse;

      if (data.response) {
        processedResponse = data.response;
      } else if (data.output) {
        processedResponse = data.output;
      } else if (data.imageUrl || data.chartUrl) {
        processedResponse = {
          imageUrl: data.imageUrl || data.chartUrl,
          caption: data.caption || data.description || '',
        };
      } else if (data.chartHtml) {
        processedResponse = {
          chartHtml: data.chartHtml,
          description: data.description || '',
        };
      } else if (data.content) {
        processedResponse = data.content;
      } else {
        processedResponse = t(
          'common.requestProcessingError',
          'Sorry, I could not process your request.'
        );
      }

      let msgChartUrl = null;
      let msgHasChart = false;

      if (typeof processedResponse === 'string') {
        const extractedData = extractDataFromText(processedResponse);
        if (extractedData) {
          msgChartUrl = generateChartUrl(extractedData);
          msgHasChart = !!msgChartUrl;
        }
        if (!msgHasChart) {
          msgHasChart = processedResponse.includes('https://quickchart.io/chart');
        }
      } else if (processedResponse && (processedResponse.chartUrl || processedResponse.chartHtml)) {
        msgHasChart = true;
      }

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: processedResponse,
        actions: data.actions || [],
        chartUrl: msgChartUrl,
        hasChart: msgHasChart,
      };

      setMessages(prev => [...prev, assistantMessage]);
      setTempDriveFile(null);

      if (data.actions && data.actions.length > 0) {
        console.log('🤖 [SUPER-BOT] Actions executed:', data.actions);
      }
    } catch (error) {
      console.error('🤖 [SUPER-BOT] Error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: t('common.botError', 'Error: {{message}}. Please try again later.', {
          message:
            error.message ||
            t('common.connectionProblem', 'There was a problem connecting to the service'),
        }),
        actions: [],
        chartUrl: null,
        hasChart: false,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const panelStyle =
    position.x !== null ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' } : {};

  return (
    <div className={styles.botContainer}>
      {isOpen && (
        <div className={styles.botInterface} ref={panelRef} style={panelStyle}>
          <div className={styles.headerBar} onMouseDown={handleDragStart}>
            <div className={styles.headerLeft}>
              <img src={rentalBuddyLogo} alt="RentalBuddy" className={styles.headerLogo} />
              <span className={styles.headerTitle}>{t('common.aiAssistant', 'AI Assistant')}</span>
            </div>
            <div className={styles.headerActions}>
              <Button
                icon
                size="mini"
                className={styles.headerButton}
                onClick={handleNewChat}
                title={t('common.newChat', 'New Chat')}
              >
                <Icon name="edit outline" />
              </Button>
              <Button
                icon
                size="mini"
                className={styles.headerButton}
                onClick={handleMinimize}
                title={t('common.minimize', 'Minimize')}
              >
                <Icon name="minus" />
              </Button>
            </div>
          </div>

          <div className={styles.messagesArea}>
            {messages.length === 0 && !isLoading && (
              <div className={styles.welcomeMessage}>
                {t('common.howCanIHelpYou', '¿Cómo puedo ayudarte hoy?')}
              </div>
            )}
            {messages.map(msg => (
              <MessageBubble key={msg.id} message={msg} />
            ))}
            {isLoading && (
              <div className={styles.assistantMessage}>
                <LoadingDots />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className={styles.inputArea}>
            {tempDriveFile && (
              <div className={styles.tempFileChip}>
                <Label size="small" className={styles.driveFileLabel}>
                  <Icon name="file" />
                  {tempDriveFile.name}
                  <Icon name="delete" onClick={() => setTempDriveFile(null)} />
                </Label>
              </div>
            )}
            <div className={styles.inputWrapper}>
              <Button
                icon
                className={styles.actionButton}
                onClick={() => setIsActionModalOpen(true)}
                title={t('common.createCardWithAI', 'Create Card with AI')}
              >
                <Icon name="plus square outline" />
              </Button>
              <Button
                icon
                className={styles.actionButton}
                onClick={() => setIsDriveModalOpen(true)}
                title="Attach Google Drive document"
              >
                <Icon name="google drive" />
              </Button>
              <Input
                fluid
                placeholder={
                  tempDriveFile
                    ? `Ask about "${tempDriveFile.name}"...`
                    : t('common.howCanIHelpYou', '¿En qué puedo ayudarte?')
                }
                value={prompt}
                onChange={handlePromptChange}
                disabled={isLoading}
                className={styles.promptInput}
                onKeyPress={e => {
                  if (e.key === 'Enter' && !isLoading) {
                    handleSubmit();
                  }
                }}
              />
              <Button
                icon
                className={styles.sendButton}
                onClick={handleSubmit}
                disabled={isLoading || !prompt.trim()}
                loading={isLoading}
              >
                <Icon name="send" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <Button
        className={`${styles.botButton}${
          messages.length > 0 && !isOpen ? ` ${styles.hasMessages}` : ''
        }`}
        onClick={handleToggle}
        title={t('common.howCanIHelpYou', 'How can I help you?')}
      >
        <div className={styles.buttonContent}>
          <img src={rentalBuddyLogo} alt="RentalBuddy" className={styles.buttonLogo} />
          <span className={styles.buttonText}>Ask anything</span>
        </div>
      </Button>

      <BotActionModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        context={typeof lastAssistantContent === 'string' ? lastAssistantContent : ''}
        initialBoardId={boardId}
        projectId={projectId}
      />

      {isDriveModalOpen && (
        <GoogleDriveModal
          onClose={() => setIsDriveModalOpen(false)}
          onSelect={file => {
            setTempDriveFile(file);
            setIsDriveModalOpen(false);
          }}
          forKnowledgeBase
        />
      )}
    </div>
  );
};

export default Bot;
